import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import ConfirmationDialog from '@Components/ConfirmationDialog';
import ShowFor from '@Components/ShowFor';
import { usePartnerReferralCodes } from '@Hooks/usePartnerReferralCodes';
import { useDeletePartnerReferralCode } from '@Hooks/useDeletePartnerReferralCode';
import type { PartnerReferralCode } from '@Types/partnerReferralCode';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { Handshake, Pencil, Plus, Trash2 } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PartnerReferralCodeSheet from './PartnerReferralCodeSheet';

const PARTNER_REFERRAL_CODE_FIELD_MAPPING: Record<string, string> = {
  code: 'filter[code]',
  description: 'filter[description]',
};

const partnerReferralCodeColumnConfigs: ColumnConfig<PartnerReferralCode>[] = [
  {
    id: 'code',
    headerLabel: 'Code',
    accessor: (row) => row.code,
    type: 'text',
    size: 180,
    cell: (info) => (
      <Link
        to={`/partner-referral-codes/${info.row.original.id}`}
        className="text-primary hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {info.getValue() as string}
      </Link>
    ),
  },
  {
    id: 'description',
    headerLabel: 'Description',
    accessor: (row) => row.description ?? '-',
    type: 'text',
    size: 240,
  },
  {
    id: 'registrationCodeIds',
    headerLabel: 'Linked Invite Codes',
    accessor: (row) => row.registrationCodeIds?.length ?? 0,
    type: 'text',
    size: 140,
    enableSorting: false,
  },
  {
    id: 'createdAt',
    headerLabel: 'Created On',
    accessor: (row) => row.createdAt,
    type: 'text',
    size: 180,
    cell: (info) => {
      const date = info.getValue() as string | undefined;
      if (!date) return 'N/A';
      return DateTime.fromISO(date).toLocaleString(DateTime.DATETIME_MED);
    },
  },
  {
    id: 'createdBy',
    headerLabel: 'Created By',
    accessor: (row) => row.createdByUser?.name || '-',
    type: 'text',
    size: 140,
    enableSorting: false,
  },
];

export default function PartnerReferralCodeList() {
  const navigate = useNavigate();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedPartnerReferralCode, setSelectedPartnerReferralCode] =
    useState<PartnerReferralCode | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [partnerReferralCodeToDelete, setPartnerReferralCodeToDelete] =
    useState<PartnerReferralCode | null>(null);

  const deleteMutation = useDeletePartnerReferralCode();

  const handleEditClick = (partnerReferralCode: PartnerReferralCode) => {
    setSelectedPartnerReferralCode(partnerReferralCode);
    setIsSheetOpen(true);
  };

  const handleDeleteClick = (partnerReferralCode: PartnerReferralCode) => {
    setPartnerReferralCodeToDelete(partnerReferralCode);
    setDeleteDialogOpen(true);
  };

  const controller = useResourceTableController<PartnerReferralCode>({
    columnsConfig: partnerReferralCodeColumnConfigs,
    defaultSort: {
      id: 'code',
      desc: false,
    },
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: PARTNER_REFERRAL_CODE_FIELD_MAPPING,
      }),
    quickFilterFields: [{ field: 'code', label: 'Code' }],
    initialPageSize: 10,
    rowActions: (row) => (
      <ShowFor permissions={['REGISTRATION_CODES_WRITE']}>
        <div className="flex gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(row);
            }}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row);
            }}
          >
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </ShowFor>
    ),
  });

  const {
    data: partnerReferralCodes,
    status,
    isError,
    error,
  } = usePartnerReferralCodes({ searchParams: controller.searchParams });

  useEffect(() => {
    if (isError && error) {
      toastError(
        error instanceof Error
          ? error.message
          : 'Failed to load Partner Referral Codes',
      );
    }
  }, [isError, error]);

  const rows: PartnerReferralCode[] = partnerReferralCodes?.data ?? [];
  const meta = partnerReferralCodes?.meta;

  const handleCreate = () => {
    setSelectedPartnerReferralCode(null);
    setIsSheetOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!partnerReferralCodeToDelete) return;

    try {
      await deleteMutation.mutateAsync({ id: partnerReferralCodeToDelete.id });
      toastSuccess(`"${partnerReferralCodeToDelete.code}" has been deleted.`);
      setDeleteDialogOpen(false);
      setPartnerReferralCodeToDelete(null);
    } catch (err) {
      toastError(
        err instanceof Error
          ? err.message
          : 'Failed to delete Partner Referral Code',
      );
    }
  };

  const handleSheetSuccess = () => {
    toastSuccess(
      selectedPartnerReferralCode
        ? `"${selectedPartnerReferralCode.code}" has been updated.`
        : 'Partner Referral Code has been created.',
    );
  };

  return (
    <>
      <ResourceTableView<PartnerReferralCode>
        label="Partner Referral Codes"
        controller={controller}
        data={rows}
        meta={meta}
        status={status}
        summaryIcon={<Handshake className="h-4 w-4 text-zinc-500" />}
        summaryLabel="Total Partner Referral Codes: "
        onRowClick={(row) => navigate(`/partner-referral-codes/${row.id}`)}
        renderExtraActions={() => (
          <ShowFor permissions={['REGISTRATION_CODES_WRITE']}>
            <Button variant="primary" onClick={handleCreate}>
              <Plus className="mr-2 h-4 w-4" />
              Create Partner Code
            </Button>
          </ShowFor>
        )}
      />

      <PartnerReferralCodeSheet
        partnerReferralCode={selectedPartnerReferralCode}
        isOpen={isSheetOpen}
        setIsOpen={setIsSheetOpen}
        onSuccess={handleSheetSuccess}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSubmit={handleDeleteConfirm}
        match={partnerReferralCodeToDelete?.code ?? ''}
        title="Delete Partner Referral Code"
        description={`Are you sure you want to delete "${partnerReferralCodeToDelete?.code}"? Existing signups keep their attribution, and the code name stays reserved.`}
        matchLabel="Type the partner code to confirm"
        submitLabel="Delete"
        isLoading={deleteMutation.isPending}
        variant="destructive"
      />
    </>
  );
}
